import React, { useState, useEffect, createContext, useRef } from "react";

import CircularProgress from "@material-ui/core/CircularProgress";
import { useNotifications } from "utils/hooks";
import withComponentLibraryTheme from "hocs/withComponentLibraryTheme";

import style from "./style.module.css";

import { Sidebar, Navbar, Footer, Modal } from "./components";
import {
  DEVICES,
  MODAL_TYPES,
  MODAL_ENUMS,
  TABS,
  SIDEBAR_KEYS,
  TEMPLATE_TAGS,
  miscellaneousMapConfig,
  FONT_LIST_DEFAULTS,
} from "./constants";
import { api, dataProvider } from "data";
import { is_empty } from "utils/validations";
import { is_desktop } from "../../../utils/Utils";
import {
  setAuthData,
  getAuthData,
  fetchAndUpdateAuth,
  getCreatorDomain,
  setNextStepsInLocalStorage,
} from "../../../utils/AuthUtil";
import {
  fetchCall,
  isWebpageDetailsFormInvalid as isFormInvalid,
  transformStateForPreview,
  isStepsChecked,
  fetchUserAuth,
} from "./utils";

import RenderHostPage from "./components/RenderHostPage";
import Loader from "./components/Loader";
import { orgPermissions, RESOURCE_KEYS } from "utils/OrgPermissions";
import { onboarding_constants } from "constants/onboardingConstant";
import { getRegexObjectFromString } from "./utils";
import { SUBDOMAIN_VALIDATION_ERROR_MSG } from "constants/displayStrings";
import { SUBDOMAIN_VALIDATION_REGEX_PATTERN } from "constants/regExpPatterns";
import { UPDATE_CREATOR_DATA_API } from "features/Common/modules/CreatorData/CreatorData.constants";
import { getWhatsNextInfo } from "features/Onboarding/modules/WhatsNext/utils/whatsNext.utils";
import { apiMethods } from "data/api.constants";
import { fetchCreatorConfig } from "features/Common/modules/CreatorData/utils/CreatorData.utils";
import { whats_next_keys } from "features/Onboarding/modules/WhatsNext/WhatsNext.constants";
import { WHATS_NEXT_APIS } from "features/Onboarding/modules/WhatsNext/WhatsNext.api";
import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";

import { logError } from "utils/error";
import { getRAUrlParams } from "features/Common/utils/url.utils";

export const WebpageCreationContext = createContext();

const TemplatePreview = () => {
  const [isFrameLoaded, setFrameLoaded] = useState(false);
  const [device, setDevice] = useState(
    is_desktop ? DEVICES.DESKTOP : DEVICES.MOBILE
  );
  const [initialState, setInitialState] = useState(null);
  const [state, setState] = useState({}); // For the user data
  const [error, setError] = useState({}); // For the user data
  const [fontList, setFontList] = useState([]);
  const [fontListMeta, setFontListMeta] = useState({
    currentPage: FONT_LIST_DEFAULTS.START_PAGE,
    totalPages: FONT_LIST_DEFAULTS.PAGE_DEFAULT,
    search: "",
    totalFonts: FONT_LIST_DEFAULTS.START_PAGE,
  });
  const [fontSearch, setFontSearch] = useState("");
  const [isFontListLoading, setIsFontListLoading] = useState(false);
  const [templateData, setTemplateData] = useState(null);

  const [testimonialData, setTestimonialData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [featuresData, setFeaturesData] = useState(null);
  const [statisticsData, setStatisticsData] = useState(null);
  const [miscellaneous_data, set_miscellaneous_data] = useState({});
  const [miscellaneousUpdate, setMiscellaneousUpdate] = useState({});

  const [modalType, setModalType] = useState(null);
  const [modalProps, setModalProps] = useState({});

  const [loadingTime, setLoadingTime] = useState(0); // How long it takes the page to load
  const [loadingTimer, setLoadingTimer] = useState(null); // The state to hold the interval timer

  const [isSaving, setIsSaving] = useState(false); // Whether the save API call is in progress
  const [hasPreviewLoadedInitially, setHasPreviewLoadedInitially] =
    useState(false); // Whether the preview has been loaded once, so as to not bombard the user
  // with messages of "preview loaded" again and again

  const isModalOpen = modalType && modalType in MODAL_TYPES;
  const tabParam = getRAUrlParams().get("tab");
  const initialTab = TABS[tabParam] || TABS.MY_PAGE;
  const [sidebar_active_tab, set_sidebar_active_tab] = useState(initialTab);
  const [is_settings_view, set_is_settings_view] = useState(false);
  const isCustomTabMode = !!TABS[tabParam];

  const disableAllEdits = !orgPermissions.hasFeatureWriteAccess(
    RESOURCE_KEYS.SUBSECTIONS.MANAGE_WEBPAGE.WEBPAGE_DETAILS
  );

  const { pushNotification } = useNotifications();

  const primaryDataToLoad = initialState && templateData;
  const secondaryDataToLoad = [
    galleryData,
    testimonialData,
    featuresData,
    statisticsData,
  ];

  const openModal = (modalTypeProp, componentProps = {}) => {
    if (modalTypeProp && modalTypeProp in MODAL_TYPES) {
      setModalType(modalTypeProp);
      setModalProps(componentProps);
    }
  };
  const closeModal = () => {
    setModalType(null);
    setModalProps({});
  };

  /** All constants are to be kept below this */

  // State to be passed down to the components. Includes the base `initialState` and the diff of the `state`
  const stateForRender = {
    ...initialState,
    ...state,
    // Since social_info is needed
    social_info: {
      ...initialState?.social_info,
      ...state?.social_info,
    },
  };

  const templateKey = initialState?.template?.slug || "modern";

  /** All functions are to be kept below this */

  const pushNotif = (messageProp, options = {}) => {
    pushNotification(messageProp, {
      ...options,
      id: messageProp,
      props: {
        variant: "default",
        color: "coral_red",
        ...(options?.props || {}),
        label: messageProp,
      },
    });
  };

  const fetchFontList = async ({
    page = FONT_LIST_DEFAULTS.PAGE_DEFAULT,
    pageSize = FONT_LIST_DEFAULTS.DEFAULT_PAGE_SIZE,
    search = "",
  } = {}) => {
    await fetchCall({
      url: `${api.fonts_list}?page=${page}&page_size=${pageSize}${
        !checkIsStringEmpty(search)
          ? `&search=${encodeURIComponent(search)}`
          : ""
      }`,
      successCallback: (res) => {
        const newPageData = checkIsArrayEmpty(res?.page_data)
          ? []
          : res.page_data;

        setFontList((prev) =>
          page === 1 ? newPageData : [...prev, ...newPageData]
        );

        setFontListMeta((prev) => ({
          ...prev,
          currentPage: page,
          totalPages: res?.total_pages ?? prev.totalPages,
          totalFonts: res?.total ?? prev.totalFonts,
          search,
        }));
      },
      errorCallback: (error) => {
        pushNotif(error?.body?.message || "Error while fetching fonts");
      },
    });
  };

  const loadMoreFonts = async () => {
    if (isFontListLoading) return;

    const nextPage = fontListMeta.currentPage + 1;
    if (nextPage > fontListMeta.totalPages) return;

    try {
      setIsFontListLoading(true);
      await fetchFontList({
        page: nextPage,
        search: checkIsStringEmpty(fontListMeta.search)
          ? ""
          : fontListMeta.search,
      });
    } finally {
      setIsFontListLoading(false);
    }
  };

  const searchTimeoutRef = useRef(null);

  const handleFontSearchChange = (searchTerm = "") => {
    setFontSearch(searchTerm);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setFontList([]);
      setFontListMeta({
        currentPage: FONT_LIST_DEFAULTS.START_PAGE,
        totalPages: FONT_LIST_DEFAULTS.PAGE_DEFAULT,
        search: checkIsStringEmpty(searchTerm) ? "" : searchTerm,
      });

      try {
        setIsFontListLoading(true);
        await fetchFontList({ page: 1, search: searchTerm });
      } finally {
        setIsFontListLoading(false);
      }
    }, 300);
  };

  const fetchMiscellaneousData = (params) => {
    const auth = getAuthData();
    let payload = {
      user__sub_domain: auth?.sub_domain,
    };
    if (!is_empty(params)) {
      Object.assign(payload, params);
    }
    fetchCall({
      url: api.creator_miscellaneous_data.get,
      payload: payload,
      successCallback: (response) => {
        if (!is_empty(response?.miscellaneous_data)) {
          // Functional update: this fetch can resolve after newer context
          // writes (e.g. a section Save); merging over a stale closure would
          // clobber them.
          set_miscellaneous_data((prev) => {
            const output = { ...(prev || {}) };
            miscellaneousMapConfig.forEach(({ type, key }) => {
              let filteredData = response?.miscellaneous_data.filter(
                (item) => item.type === type
              );
              if (!is_empty(filteredData)) {
                Object.assign(output, { [key]: filteredData });
              }
            });
            return { ...output };
          });
        }
      },
      errorCallback: (error) => {
        pushNotif(error?.body?.message || "Error while fetching data");
      },
    });
  };

  const fetchUserData = () => {
    const auth = getAuthData();
    const userID = auth?.username;
    fetchMiscellaneousData();
    return fetchCall({
      url: `${api.get_creator_page_details}/${userID}`,
      successCallback: (userData) => {
        setInitialState(userData);
        setState({
          ...state,
          colors: {
            ...(userData?.colors && Object.keys(userData?.colors || {})?.length
              ? userData?.colors
              : userData?.theme?.color_codes),
          },
          themeSlug: userData?.theme?.slug || "modern-red",
          lead_keys: {
            expand_host_desc: !!userData?.expand_host_desc,
          },
        });
        return userData;
      },
      errorCallback: (error) => {
        pushNotif(error?.body?.message || "Error while fetching user data");
      },
    });
  };

  const fetchTemplateData = (templateProp) =>
    fetchCall({
      url: `${api.get_template_details}${templateProp || templateKey}`,
      method: "GET",
      successCallback: setTemplateData,
      errorCallback: () =>
        pushNotif(
          error?.body?.message || "Error while fetching user's webpage data"
        ),
    });

  const sortByPriority = (a, b) => (b?.priority ?? 0) - (a?.priority ?? 0);

  const fetchTestimonials = () =>
    fetchCall({
      url: api.fetch_testimonial,
      method: "GET",
      successCallback: (respData) => {
        setTestimonialData(respData.testimonials.sort(sortByPriority));
      },
      errorCallback: () => {
        pushNotif(
          error?.body?.message || "Error while fetching testimonial data"
        );
      },
    });

  const fetchGallery = () =>
    fetchCall({
      url: api.fetch_gallery,
      method: "GET",
      successCallback: (respData) => {
        setGalleryData(
          respData.gallery
            .map((g) => ({
              ...g,
              type: g.type === "photo" ? "image" : g.type,
            }))
            .sort(sortByPriority)
        );
      },
      errorCallback: () => {
        pushNotif(error?.body?.message || "Error while fetching gallery data");
      },
    });

  const fetchFeatures = () =>
    fetchCall({
      url: api.fetch_feature,
      method: "GET",
      successCallback: (respData) => {
        setFeaturesData(respData.features.sort(sortByPriority));
      },
      errorCallback: () =>
        pushNotif(error?.body?.message || "Error while fetching feature data"),
    });

  const fetchStatistics = () =>
    fetchCall({
      url: api.fetch_statistic,
      method: "GET",
      successCallback: (respData) => {
        setStatisticsData(respData.statistic.sort(sortByPriority));
      },
      errorCallback: () => {
        pushNotif(
          error?.body?.message || "Error while fetching statistics data"
        );
      },
    });

  const handleThemeChange = async (themeProp) => {
    if (templateKey && themeProp)
      return fetchCall({
        url: `${api.update_selected_theme}`,
        method: "POST",
        payload: {
          template: templateKey,
          theme: themeProp,
        },
      });
  };

  const updatePreview = () => {
    const pageFrame = document.getElementById("pageFrame");
    if (pageFrame && isFrameLoaded) {
      const { ...stateToSend } = stateForRender;
      const transformedState = transformStateForPreview({
        stateProp: stateToSend,
        testimonialData,
        galleryData,
        featuresData,
        statisticsData,
      });
      pageFrame.contentWindow.postMessage(transformedState, "*");
    }
  };

  const updateMiscellaneousPreview = () => {
    const pageFrame = document.getElementById("pageFrame");
    if (pageFrame && isFrameLoaded) {
      pageFrame.contentWindow.postMessage(
        {
          type: "miscellaneous_data",
          miscellaneous_data,
        },
        "*"
      );
    }
  };

  const reloadPreview = () => {
    const pageFrame = document.getElementById("pageFrame");
    pageFrame.src = `${pageFrame.src}`;
  };

  const handleScrollToPreview = () => {
    const pageFrame = document.getElementById("pageFrame");
    pageFrame.scrollIntoView({
      block: "end",
    });
  };

  const handleFontUpdate = async (fontProp) => {
    const userID = localStorage.getItem("uuid");
    return await fetchCall({
      url: `${api.update_user}${userID}`,
      method: "POST",
      payload: {
        source: "exly",
        updated_values: {
          font: fontProp,
        },
      },
      successCallback: () => true,
      errorCallback: (error) => {
        pushNotif(error?.body?.message || "Error while updating font");
        return false;
      },
    });
  };

  const checkSubdomainValid = (newSubdomain) =>
    fetchCall({
      url: api.check_availability,
      payload: { sub_domain: newSubdomain },
      successCallback: () => ({ isAvailable: true, message: null }),
      errorCallback: (error) => {
        return {
          isAvailable: false,
          message: error?.body?.message || "Subdomain unavailable",
        };
      },
    });

  const setErrorMessage = (fieldProp, errorMessage, otherProps = {}) => {
    if (!fieldProp) return;

    const { isSuccess = false, isInfo = false } = otherProps || {};

    setError((errorState) => ({
      ...errorState,
      [fieldProp]: {
        ...errorState?.[fieldProp],
        error: errorMessage ?? null,
        isSuccess: isSuccess ?? false,
        isInfo: isInfo ?? false,
      },
    }));
  };

  const updateSubdomain = async (newSubdomain, showErrorPopup = false) => {
    const userID = localStorage.getItem("uuid");
    const { isAvailable, message } = await checkSubdomainValid(
      newSubdomain,
      showErrorPopup
    );
    if (isAvailable) {
      return await fetchCall({
        url: `${api.update_user}${userID}`,
        method: "POST",
        payload: {
          source: "exly",
          updated_values: {
            sub_domain: newSubdomain,
            social_info: {
              ...getAuthData().social_info,
              is_auto_subdomain: false,
            },
          },
        },
        successCallback: () => {
          fetchCreatorConfig(); /// so as to update latest value in session storage as well
          setInitialState({
            ...initialState,
            sub_domain: newSubdomain,
          });
          // Update the subdomain in localstorage since that
          // is what is used in the rest of the app
          try {
            if (newSubdomain) {
              window.localStorage.setItem("sub_domain", newSubdomain);
            }

            const authData = getAuthData();
            if (authData && newSubdomain) {
              authData.sub_domain = newSubdomain;
              authData.social_info.is_auto_subdomain = false;
              setAuthData(authData);
            }
          } catch (error) {
            console.error(
              "Error while updating subdomain in localstorage",
              error
            );
          }
          setErrorMessage("sub_domain", null);
          pushNotif("Subdomain updated successfully!", {
            variant: "default",
            color: "fine_pine",
          });
          return true;
        },
        errorCallback: (error) => {
          if (showErrorPopup) {
            pushNotif(
              error?.body?.message ||
                "Unable to update subdomain. Please try again."
            );
          }
        },
      });
    } else {
      if (showErrorPopup) {
        pushNotif(message || "Subdomain unavailable");
      }
    }
    return false;
  };

  const handleLoadTags = async () => {
    return await fetchUserAuth((userDataProp) => {
      const userTags = userDataProp?.social_info?.template_tags;
      const tagList = TEMPLATE_TAGS.map((tag) => tag.slug);
      const tagData = userTags
        ? userTags.filter((t) => tagList.includes(t))
        : userTags;
      try {
        const nextSteps = getWhatsNextInfo();
        if (
          !userDataProp?.theme &&
          !nextSteps?.store_website_design &&
          !(!is_empty(tagData) && Array.isArray(tagData))
        ) {
          window.location.href = `${window.location.origin}/#/Templates/selectTag?onboarding=true`;
        }
      } catch (error) {
        console.error("Error", error);
      }
    });
  };

  // When the iframe is fully loaded
  const handleFrameLoad = () => {
    setFrameLoaded(true);
    if (!hasPreviewLoadedInitially) {
      pushNotif("Preview loaded!");
      setHasPreviewLoadedInitially(true);
    }
    updatePreview();
  };

  const setField = (selector, value) => {
    if (
      typeof selector === "string" ||
      (Array.isArray(selector) && selector.length === 1)
    ) {
      setState({
        ...state,
        [Array.isArray(selector) ? selector[0] : selector]: value,
      });
    } else if (Array.isArray(selector) && selector.length === 2) {
      setState({
        ...state,
        [selector[0]]: { ...state[selector[0]], [selector[1]]: value },
      });
    }
  };

  /**
   * On change of any section and user directly clicks on update without saving,
   * this function hits the update api for changed sections.
   * @returns {*} Resolved promise which updates all changed sections
   */
  const handleUpdateMiscellaneous = async () => {
    // Creating array of promises of handleSave for all updated sections, to be resolved furthur
    const updateData = Object.keys(miscellaneousUpdate)?.map((item) =>
      miscellaneousUpdate[item]?.handleSave()
    );
    return await Promise.all(updateData)
      .then(() => {
        setMiscellaneousUpdate({});
      })
      .catch((err) => console.error(err));
  };

  const handleSave = async (saveMessage = "Saved") => {
    try {
      if (
        isFormInvalid({
          stateProp: stateForRender,
          setError,
          pushNotif,
        })
      ) {
        return false;
      }

      setIsSaving(true);

      const userID = localStorage.getItem("uuid");
      // Extracting the fields not meant to be sent in the API
      const {
        display_name,
        social_info,
        font,
        // eslint-disable-next-line no-unused-vars
        themeSlug,
        reset_colors,
        ...restOfState
      } = state;

      // Prune out the values that are same as before
      const prunedState = {};

      if (font && initialState?.font?.slug !== font?.slug) {
        await handleFontUpdate(font.slug);
      }

      if (
        state?.sub_domain &&
        initialState?.sub_domain &&
        initialState.sub_domain !== state.sub_domain
      ) {
        if (state?.sub_domain?.length < 2) {
          setErrorMessage("sub_domain", "Must be at least 2 characters");
          pushNotif("Subdomain must be at least 2 characters");
          setIsSaving(false);
          return false;
        }

        if (
          state?.sub_domain?.length > onboarding_constants.sub_domain.maxLength
        ) {
          setErrorMessage(
            "sub_domain",
            `Cannot exceed ${onboarding_constants.sub_domain.maxLength} characters. (Currently ${state?.sub_domain.length})`
          );
          pushNotif(
            `Subdomain cannot exceed ${onboarding_constants.sub_domain.maxLength} characters. (Currently ${state?.sub_domain.length})`
          );
          setIsSaving(false);
          return false;
        }
        // Only allow chars and numbers and hyphens between characters/numbers and
        // no other special characters
        const isValidSubDomain = getRegexObjectFromString(
          SUBDOMAIN_VALIDATION_REGEX_PATTERN,
          "g"
        ).test(state?.sub_domain);

        if (!isValidSubDomain) {
          setErrorMessage("sub_domain", SUBDOMAIN_VALIDATION_ERROR_MSG);
          pushNotif(SUBDOMAIN_VALIDATION_ERROR_MSG);
          setIsSaving(false);
          return false;
        }

        const isUpdated = await updateSubdomain(state.sub_domain, true);
        if (!isUpdated) {
          setIsSaving(false);
          return false;
        }
      }

      Object.keys(restOfState).forEach((stateKey) => {
        if (
          !(stateKey in initialState) ||
          (stateKey in initialState &&
            initialState[stateKey] !== restOfState[stateKey])
        ) {
          prunedState[stateKey] = restOfState[stateKey];
        }
      });

      // reset_colors acts as a reset flag, so must be added in manually when needed
      if (
        reset_colors &&
        JSON.stringify(state.colors) ===
          JSON.stringify(initialState?.theme?.codes?.colors)
      ) {
        prunedState.reset_colors = true;
      }

      const leadKeyData = [
        "interest_cta_text", // The text on the get in touch button
        "interest_helper_text", // The text for the header section
        "features_image", // Image for features section
        "features_description", // Description for features section
      ]
        .filter(
          (key) =>
            social_info &&
            key in social_info &&
            initialState?.social_info?.[key] !== social_info[key]
        )
        .map((key) => ({
          [key]: social_info[key],
        }));

      if (prunedState.custom_headings) {
        leadKeyData.push({
          custom_headings: prunedState.custom_headings,
        });
        delete prunedState.custom_headings;
      }

      const reqPayload = {
        updated_values: {
          ...prunedState,
          ...(social_info && {
            social_info: {
              ...initialState?.social_info,
              ...social_info,
            },
          }),
          ...(leadKeyData.length > 0 && {
            lead_keys: Object.assign({}, ...leadKeyData),
          }),
          ...(display_name &&
            display_name !== initialState?.display_name && {
              first_name: display_name?.split(" ")?.[0] || "",
              last_name: display_name?.includes(" ")
                ? display_name?.slice(display_name?.indexOf(" "))?.trim()
                : "",
            }),
          website_design_completed_status: null,
        },
        source: "exly",
      };

      const modifiedInitialState = Object.assign(
        {},
        initialState,
        reqPayload.updated_values
      );
      reqPayload.updated_values.website_design_completed_status =
        isStepsChecked(
          modifiedInitialState,
          SIDEBAR_KEYS,
          testimonialData,
          galleryData,
          featuresData
        )[1]
          ? null
          : true;

      const successCallback = async () => {
        // when logo updates need updated logo in auth, so updating auth on update
        fetchAndUpdateAuth().catch((err) => {
          logError({
            error: err?.message || err?.body?.message,
            occuredAt: "fetchAndUpdateAuth in src/ui/layout/Menu/Menu.jsx",
            when: "Error while fetching fetchAndUpdateAuth",
          });
        });

        const nextSteps = getWhatsNextInfo();
        // setting up the next steps flag here as we need to check the value of website design flag
        if (!nextSteps?.store_website_design) {
          dataProvider
            .custom_request(WHATS_NEXT_APIS.GET_PROGRESS, apiMethods.GET, {
              request_params: JSON.stringify([
                whats_next_keys.store_website_design.key,
              ]),
            })
            .then((resp) => {
              if (resp?.data?.store_website_design === true) {
                nextSteps.store_website_design = true;
                setNextStepsInLocalStorage({ data: nextSteps });
              }
            })
            .catch((err) => {
              console.error(
                "error in fetching get list at website template",
                err
              );
            });
        }
        if (Object.keys(miscellaneousUpdate)?.length) {
          await handleUpdateMiscellaneous();
        }
        pushNotif(saveMessage, {
          variant: "default",
          color: "fine_pine",
        });

        setInitialState({ ...stateForRender });
        setState({
          colors: state?.colors,
          themeSlug: state?.themeSlug,
        });

        updatePreview();
        setIsSaving(false);
        return true;
      };

      // If there is nothing to update, just call the successCallback
      if (Object.keys(reqPayload.updated_values).length === 0) {
        return successCallback();
      }

      return await fetchCall({
        url: `${UPDATE_CREATOR_DATA_API}/${userID}`,
        method: "POST",
        payload: reqPayload,
        successCallback,
        errorCallback: (error) => {
          pushNotif(
            error?.body?.message
              ? `Error Saving: ${error?.body?.message}`
              : "Error saving webpage"
          );
          setIsSaving(false);
          return false;
        },
      });
    } catch (error) {
      pushNotif(error?.body?.message || "Error saving webpage");
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    // Safari blocks window.open calls in async functions. Have to use .then syntax
    // https://stackoverflow.com/questions/20696041/window-openurl-blank-not-working-on-imac-safari
    handleSave().then((isSaved) => {
      if (isSaved) {
        const hostUrl = `//${
          initialState.sub_domain
        }.${getCreatorDomain()}?preview=true`;
        const a = document.createElement("a");
        a.setAttribute("href", hostUrl);
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noreferrer noopener");
        a.dispatchEvent(
          new MouseEvent("click", {
            view: window,
            bubbles: true,
            cancelable: true,
          })
        );
      }
    });
  };

  const enableError = (fieldProp, isErrorEnabled = true) => {
    if (!fieldProp) return;
    setError((errorState) => ({
      ...errorState,
      [fieldProp]: {
        ...errorState?.[fieldProp],
        showError: !!isErrorEnabled ?? true,
      },
    }));
  };

  /** All useEffects are to be kept below this */

  useEffect(() => {
    (async () => {
      // Fetch the user, template, and font data on initial render

      const userData = await fetchUserData();
      localStorage.setItem("sub_domain", userData?.sub_domain);

      // Fire all the API calls in parallel for faster loading
      setIsFontListLoading(true);
      await Promise.all([
        fetchFontList({
          page: FONT_LIST_DEFAULTS.PAGE_DEFAULT,
          search: fontSearch,
        }),
        fetchTemplateData(userData?.template?.slug),
      ]);
      setIsFontListLoading(false);

      setInterval(() => {
        updatePreview();
      }, 5000);

      Promise.all([
        fetchTestimonials(),
        fetchGallery(),
        fetchFeatures(),
        fetchStatistics(),
      ]);
    })();

    if (!loadingTimer) {
      setLoadingTimer(
        setInterval(() => {
          setLoadingTime((loadingTimeProp) => loadingTimeProp + 1);
        }, 1000)
      );
    }

    handleLoadTags();
  }, []);

  useEffect(() => {
    if (primaryDataToLoad) {
      clearInterval(loadingTimer);
      setLoadingTimer(null);
      setLoadingTime(-1);
    }
  }, [primaryDataToLoad]);

  useEffect(() => {
    updatePreview();
  }, [
    state,
    isFrameLoaded,
    galleryData,
    testimonialData,
    featuresData,
    statisticsData,
  ]);

  useEffect(() => {
    updateMiscellaneousPreview();
  }, [miscellaneous_data]);

  useEffect(() => {
    // Do not run this on the initial render
    if (
      !initialState?.theme?.slug ||
      state?.themeSlug === initialState?.theme?.slug
    )
      return;

    (async () => {
      try {
        await handleThemeChange(state?.themeSlug);
        pushNotif("Theme Changed!");

        // Fetch the new theme data
        await fetchUserData();
        updatePreview();
      } catch (error) {
        pushNotif(error?.body?.message || "Error changing theme");
      }
    })();

    setFrameLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.themeSlug]);

  const sidebarProps = {
    handleSave,
    state: stateForRender,
    initialState,
    setState,
    setField,
    errorState: error,
    setErrorMessage,
    enableError,
    disableAllEdits,
  };

  const navbarProps = {
    handlePreview,
    handleSave,
    device,
    setDevice,
    templateName: templateData?.title || templateKey,
    disableAllEdits,
    isCustomTabMode,
  };

  // To be passed as WebpageCreationContext
  const contextProps = {
    sidebar_active_tab,
    set_sidebar_active_tab,
    is_settings_view,
    set_is_settings_view,
    device,
    setDevice,
    fontList,
    fontListMeta,
    templateData,
    miscellaneous_data,
    fetchMiscellaneousData,
    set_miscellaneous_data,
    setMiscellaneousUpdate,
    fetchCall,
    pushNotif,

    loadMoreFonts,
    isFontListLoading,
    fontSearch,
    handleFontSearchChange,

    isSaving,

    subdomain: {
      updateSubdomain,
      checkSubdomainValid,
    },

    testimonials: {
      testimonialData,
      setTestimonialData,
    },
    gallery: {
      galleryData,
      setGalleryData,
    },

    features: { featuresData, setFeaturesData },
    statistics: { statisticsData, setStatisticsData },

    modal: {
      isModalOpen,
      modalType,
      openModal,
      closeModal,
      modalProps,
      setModalProps,
    },
  };

  const ModalComponent =
    modalType && modalType in MODAL_TYPES ? MODAL_ENUMS[modalType] : null;

  return primaryDataToLoad ? (
    <WebpageCreationContext.Provider value={contextProps}>
      <div className={style.root}>
        <Navbar {...navbarProps} />

        <div className={style.container}>
          <Sidebar {...sidebarProps}>
            <Footer
              handleSave={handleSave}
              handlePreview={handlePreview}
              isSaving={isSaving}
              disableAllEdits={disableAllEdits}
            />
          </Sidebar>

          {sidebar_active_tab !== TABS.REORDER && (
            <RenderHostPage
              isFrameLoaded={isFrameLoaded}
              initialState={initialState}
              device={device}
              stateForRender={stateForRender}
              templateKey={templateKey}
              reloadPreview={reloadPreview}
              handleFrameLoad={handleFrameLoad}
              handleScrollToPreview={handleScrollToPreview}
            />
          )}
        </div>

        <Modal
          isModalOpen={isModalOpen}
          handleModalOpen={openModal}
          handleModalClose={closeModal}
          modalProps={modalProps}
          pushNotif={pushNotif}
        >
          {isModalOpen && modalType && modalType in MODAL_TYPES ? (
            <ModalComponent
              {...modalProps}
              LoadingComponent={() => (
                <div className={style.modalLoader}>
                  <CircularProgress thickness={1.5} color="secondary" />
                </div>
              )}
            />
          ) : null}
        </Modal>
      </div>
    </WebpageCreationContext.Provider>
  ) : (
    <Loader
      initialState={initialState}
      templateData={templateData}
      secondaryDataToLoad={secondaryDataToLoad}
      loadingTime={loadingTime}
      primaryDataToLoad={primaryDataToLoad}
    />
  );
};

export default withComponentLibraryTheme(TemplatePreview);
